/**
 * Admin · Paint Aliases (PAINT-SEARCH-02).
 *
 * Three tabs:
 *  1. Aliases — paginated list, brand filter, search, delete
 *  2. Import CSV — upload a `brand,trade_name,ref_hex[,library_code][,notes]`
 *     file; server validates + resolves nearest library colour + upserts
 *  3. Search misses — queries that returned zero results, ranked by count;
 *     "Resolve" opens a create-alias form pre-populated with the query
 *
 * Access: admin+ (Owner or Admin).
 */

import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Upload, RefreshCw, Search, ChevronRight, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const TABS = [
  { key: "aliases", label: "Aliases" },
  { key: "import",  label: "Import CSV" },
  { key: "misses",  label: "Search misses" },
];

export default function AdminPaintAliases() {
  const [tab, setTab] = useState("aliases");

  return (
    <div className="space-y-6" data-testid="admin-paint-aliases">
      <div>
        <div className="overline mb-2 text-brand-teal">PAINT-SEARCH-02</div>
        <h2 className="font-serif text-2xl text-brand-ink">Paint aliases</h2>
        <p className="text-xs text-brand-muted mt-1">
          Brand → ColourMatch library mapping. Trade colour names never render as swatches — they only help customers find the closest approved ColourMatch colour.
        </p>
      </div>

      <div className="flex gap-1 border-b border-brand-linen">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`aliases-tab-${t.key}`}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
              tab === t.key
                ? "border-brand-teal text-brand-teal"
                : "border-transparent text-brand-muted hover:text-brand-ink"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "aliases" && <AliasesTab />}
      {tab === "import" && <ImportTab />}
      {tab === "misses" && <MissesTab />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 1 — Aliases
// ---------------------------------------------------------------------------
function AliasesTab() {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/paint-aliases", {
        params: { q: q || undefined, brand: brand || undefined, page, page_size: pageSize },
      });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Load failed.");
      setRows([]);
    }
  }, [q, brand, page]);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this alias?")) return;
    try {
      await api.delete(`/admin/paint-aliases/${id}`);
      toast.success("Alias deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed.");
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
          <Input
            data-testid="aliases-search"
            value={q}
            onChange={(e) => { setPage(1); setQ(e.target.value); }}
            placeholder="Search trade name…"
            className="pl-9"
          />
        </div>
        <Input
          data-testid="aliases-brand-filter"
          value={brand}
          onChange={(e) => { setPage(1); setBrand(e.target.value); }}
          placeholder="Filter by brand (Dulux, Resene, …)"
          className="sm:w-64"
        />
        <Button variant="outline" onClick={load} data-testid="aliases-reload">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      <div className="text-xs text-brand-muted" data-testid="aliases-count">
        {total.toLocaleString()} alias{total === 1 ? "" : "es"}
      </div>

      {rows === null ? (
        <div className="text-sm text-brand-muted">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-brand-muted p-6 border border-dashed border-brand-linen bg-white text-center">
          No aliases match this filter.
        </div>
      ) : (
        <div className="border border-brand-linen bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-brand-linen/40 text-brand-muted text-left">
              <tr>
                <th className="px-3 py-2 font-medium">Brand</th>
                <th className="px-3 py-2 font-medium">Trade name</th>
                <th className="px-3 py-2 font-medium">Ref hex</th>
                <th className="px-3 py-2 font-medium">Library colour</th>
                <th className="px-3 py-2 font-medium text-right">ΔE</th>
                <th className="px-3 py-2 font-medium">Source</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`alias-row-${r.id}`} className="border-t border-brand-linen">
                  <td className="px-3 py-2 text-brand-ink">{r.brand}</td>
                  <td className="px-3 py-2 text-brand-ink font-medium">{r.trade_name}</td>
                  <td className="px-3 py-2 font-mono text-xs">
                    <span className="inline-block w-3 h-3 rounded-sm border border-brand-linen mr-1.5 align-middle" style={{ background: r.ref_hex }} />
                    {r.ref_hex}
                  </td>
                  <td className="px-3 py-2">
                    <span className="inline-block w-3 h-3 rounded-sm border border-brand-linen mr-1.5 align-middle" style={{ background: r.library_hex }} />
                    <span className="text-brand-ink">{r.library_name}</span>
                    <span className="text-brand-muted ml-1 font-mono text-[10px]">{r.library_code}</span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{typeof r.delta_e === "number" ? r.delta_e.toFixed(2) : "—"}</td>
                  <td className="px-3 py-2 text-[11px] uppercase tracking-wide text-brand-muted">{r.source}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => del(r.id)}
                      data-testid={`alias-delete-${r.id}`}
                      className="text-brand-muted hover:text-red-600 transition-colors"
                      title="Delete alias"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pageCount > 1 && (
        <div className="flex items-center justify-between text-xs">
          <span className="text-brand-muted">Page {page} of {pageCount}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</Button>
            <Button variant="outline" size="sm" disabled={page >= pageCount} onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 2 — CSV Import
// ---------------------------------------------------------------------------
function ImportTab() {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Select a CSV file first."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/paint-aliases/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`${data.inserted} added · ${data.updated} updated · ${data.errors.length} errors`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Import failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="border border-brand-linen bg-brand-linen/20 p-4 text-sm">
        <div className="font-medium text-brand-ink mb-1">CSV format</div>
        <code className="block font-mono text-xs bg-white border border-brand-linen p-2 mb-2">brand,trade_name,ref_hex[,library_code][,notes]</code>
        <ul className="text-xs text-brand-muted space-y-1">
          <li>• <strong>brand</strong>, <strong>trade_name</strong>, <strong>ref_hex</strong> are required.</li>
          <li>• <strong>library_code</strong> is optional — if blank, the closest active ColourMatch library colour is resolved automatically by LAB delta-E.</li>
          <li>• Existing rows with the same <code>(brand, trade_name)</code> are updated in place — safe to re-run.</li>
          <li>• Trade colour hex is used only for finding the closest ColourMatch colour. It is never rendered as a swatch.</li>
        </ul>
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="csv-file" className="text-brand-ink">CSV file</Label>
          <Input
            id="csv-file"
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="aliases-csv-input"
            className="mt-1.5"
          />
        </div>
        <Button
          type="submit"
          disabled={!file || busy}
          data-testid="aliases-csv-submit"
          className="bg-brand-navy text-white hover:bg-brand-teal/90"
        >
          <Upload className="w-4 h-4 mr-2" />
          {busy ? "Importing…" : "Import"}
        </Button>
      </form>

      {result && (
        <div className="border border-brand-linen bg-white p-4" data-testid="aliases-import-result">
          <div className="text-sm text-brand-ink font-medium mb-2">Import complete</div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <Stat label="Added" value={result.inserted} />
            <Stat label="Updated" value={result.updated} />
            <Stat label="Errors" value={result.errors.length} tone={result.errors.length ? "warn" : "ok"} />
          </div>
          {result.errors.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-brand-muted mb-2">View errors ({result.errors.length})</summary>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {result.errors.map((e, i) => (
                  <div key={i} className="border border-red-200 bg-red-50 p-2">
                    Row {e.row}: {e.error}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone = "ok" }) {
  const cls = tone === "warn" && value > 0 ? "text-red-600" : "text-brand-ink";
  return (
    <div className="border border-brand-linen p-3">
      <div className="overline mb-1">{label}</div>
      <div className={`font-serif text-2xl ${cls}`}>{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 3 — Search misses
// ---------------------------------------------------------------------------
function MissesTab() {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [showResolved, setShowResolved] = useState(false);
  const [resolveMiss, setResolveMiss] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/paint-aliases/misses", {
        params: { resolved: showResolved ? undefined : false, page_size: 50 },
      });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Load failed.");
      setRows([]);
    }
  }, [showResolved]);

  useEffect(() => { load(); }, [load]);

  const dismiss = async (id) => {
    if (!window.confirm("Dismiss this miss without creating an alias?")) return;
    try {
      await api.delete(`/admin/paint-aliases/misses/${id}`);
      toast.success("Miss dismissed");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Dismiss failed.");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="text-xs text-brand-muted flex-1" data-testid="misses-count">
          {total.toLocaleString()} {showResolved ? "total" : "unresolved"} miss{total === 1 ? "" : "es"}
        </div>
        <label className="flex items-center gap-2 text-xs text-brand-muted cursor-pointer">
          <input
            type="checkbox"
            checked={showResolved}
            onChange={(e) => setShowResolved(e.target.checked)}
            data-testid="misses-show-resolved"
          />
          Include resolved
        </label>
        <Button variant="outline" size="sm" onClick={load} data-testid="misses-reload">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {rows === null ? (
        <div className="text-sm text-brand-muted">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-brand-muted p-6 border border-dashed border-brand-linen bg-white text-center">
          No search misses yet. Once customers type unrecognised colour names they&apos;ll appear here.
        </div>
      ) : (
        <div className="border border-brand-linen bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-brand-linen/40 text-brand-muted text-left">
              <tr>
                <th className="px-3 py-2 font-medium">Query</th>
                <th className="px-3 py-2 font-medium text-right">Count</th>
                <th className="px-3 py-2 font-medium">Last seen</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`miss-row-${r.id}`} className="border-t border-brand-linen">
                  <td className="px-3 py-2 text-brand-ink">
                    <span className="font-mono text-xs">&ldquo;{r.query_normalized}&rdquo;</span>
                    {r.raw_examples?.length > 1 && (
                      <span className="ml-2 text-brand-muted text-[10px]">+{r.raw_examples.length - 1} variants</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{r.count}</td>
                  <td className="px-3 py-2 text-brand-muted text-xs">{r.last_seen?.slice(0, 10) || "—"}</td>
                  <td className="px-3 py-2">
                    {r.resolved ? (
                      <span className="text-[10px] uppercase tracking-wide text-brand-teal">Resolved</span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wide text-brand-muted">Open</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {!r.resolved && (
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => setResolveMiss(r)}
                          data-testid={`miss-resolve-${r.id}`}
                          className="text-xs text-brand-teal hover:underline inline-flex items-center gap-1"
                        >
                          Resolve <ArrowRight className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => dismiss(r.id)}
                          data-testid={`miss-dismiss-${r.id}`}
                          className="text-brand-muted hover:text-red-600"
                          title="Dismiss"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {resolveMiss && (
        <ResolveMissDialog
          miss={resolveMiss}
          onClose={() => setResolveMiss(null)}
          onResolved={() => { setResolveMiss(null); load(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Resolve miss → create alias dialog
// ---------------------------------------------------------------------------
function ResolveMissDialog({ miss, onClose, onResolved }) {
  // Try to guess brand from the first word if it matches a known brand.
  const guessBrand = (() => {
    const known = ["Dulux", "Resene", "Taubmans", "Wattyl", "Haymes", "Bristol"];
    const first = (miss.query_normalized.split(" ")[0] || "").toLowerCase();
    return known.find((b) => b.toLowerCase() === first) || "";
  })();
  const initialName = (() => {
    const parts = miss.query_normalized.split(" ");
    if (parts.length > 1 && guessBrand) return parts.slice(1).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
    return miss.query_normalized.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  })();

  const [brand, setBrand] = useState(guessBrand);
  const [tradeName, setTradeName] = useState(initialName);
  const [refHex, setRefHex] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!brand.trim() || !tradeName.trim() || !refHex.trim()) {
      toast.error("Brand, trade name and ref hex are required.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/paint-aliases/misses/${miss.id}/resolve`, {
        brand: brand.trim(),
        trade_name: tradeName.trim(),
        ref_hex: refHex.trim(),
      });
      toast.success("Alias created — miss resolved");
      onResolved();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Resolve failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="miss-resolve-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-brand-ink">Resolve search miss</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-brand-muted mt-2 mb-4">
            <div className="font-mono">&ldquo;{miss.query_normalized}&rdquo;</div>
            <div className="mt-1">Seen {miss.count} time{miss.count === 1 ? "" : "s"}. Fill in the brand, trade name and a reference hex — the closest ColourMatch library colour will be resolved automatically.</div>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-brand-ink">Brand</Label>
              <Input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Dulux" data-testid="resolve-brand" className="mt-1" />
            </div>
            <div>
              <Label className="text-brand-ink">Trade name</Label>
              <Input value={tradeName} onChange={(e) => setTradeName(e.target.value)} placeholder="Lexicon Quarter" data-testid="resolve-trade" className="mt-1" />
            </div>
            <div>
              <Label className="text-brand-ink">Reference hex</Label>
              <Input value={refHex} onChange={(e) => setRefHex(e.target.value)} placeholder="#EEEEE1" data-testid="resolve-hex" className="mt-1 font-mono" />
              <p className="text-[11px] text-brand-muted mt-1">Approximation used only for finding the closest ColourMatch library colour.</p>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-brand-navy text-white hover:bg-brand-teal/90" data-testid="resolve-submit">
              {busy ? "Resolving…" : "Resolve"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
